/* eslint-disable no-underscore-dangle */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
// adopt
import { adopt } from 'react-adopt';
// graphQL
import gql from 'graphql-tag';
// our components
import qs, { getQueryString } from 'helpers/queryStrings';
import RouterContext from 'helpers/routerContext';
import { Query } from 'components/common/Query';
import { PageInfo } from 'components/common/Paginator';
import PaidAdReportDetails from './Details';

/**
 * Query to fetch agency summary from api
 */
export const AGENCY_SUMMARY_QUERY = gql`
  query PaidAdReportDetails(
    $startDateGte: DateTime
    $startDateLte: DateTime
    $status: [AutoPaidAdStatusChoices]
    $first: Int
    $last: Int
    $before: String
    $after: String
    $id: ID!
  ) {
    agencySummaries(
      id: $id
      startDateGte: $startDateGte
      startDateLte: $startDateLte
    ) {
      agency {
        id
        fullname
        email
        photo {
          gcsKey
          url
        }
        paymentMethod
        autoRenews: autoRenews(
          startDateGte: $startDateGte
          startDateLte: $startDateLte
          status: $status
          first: $first
          last: $last
          before: $before
          after: $after
        ) {
          pageInfo {
            ...PaginatorPageInfo
          }
          edges {
            node {
              id
              startDate
              endDate
              owner {
                id
                fakeId
                publicId
                fullname
              }
              manager {
                id
                fullname
                userType
              }
              hasPromotion
              duration {
                years
                months
                days
                hours
                minutes
                seconds
              }
              paidAdType
              ... on AdminPromotedAdType {
                grossSale
                netSale
                secondaryGrossSale
                secondaryNetSale
                secondaryCommission
                locationScope
              }
              ... on AdminAutoPaidAdType {
                schedule
                frequency {
                  years
                  months
                  days
                  hours
                  minutes
                  seconds
                }
                grossSale
                netSale
                secondaryGrossSale
                secondaryNetSale
                secondaryCommission
              }
              status
              commission
              currency
              secondaryCurrency
              ad {
                id
                title
                subcategory {
                  id
                  title
                  parentCategory {
                    id
                    title
                  }
                }
                readyImages {
                  id
                  urls {
                    high
                    thumb
                  }
                }
                updatedOnToOrder
                flagCount
                status
                reviewed
                updateCount
              }
            }
          }
        }
      }
      totalSummaries {
        grossSales
        netSales
        commissions
        count
        currency
      }
      paidAdSummaries {
        grossSales
        netSales
        commissions
        count
        currency
      }
      creditBundleSummaries {
        grossSales
        netSales
        commissions
        count
        currency
      }
    }
  }
  ${PageInfo}
`;

/**
 * @class
 * @extends Component
 */
class PaidAdReportDetailsContainer extends Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    const { initialFilters } = props;
    const defaultFilters = {
      startDateGte: moment()
        .startOf('month')
        .format(),
      startDateLte: moment()
        .endOf('month')
        .format(),
    };
    const startDateGte =
      initialFilters.startDateGte !== undefined
        ? initialFilters.startDateGte
        : defaultFilters.startDateGte;
    const startDateLte =
      initialFilters.startDateLte !== undefined
        ? initialFilters.startDateLte
        : defaultFilters.startDateLte;

    const {
      numberOfRowsSelected,
      direction,
      cursor,
      ...initFilters
    } = initialFilters;
    this.state = {
      numberOfRowsSelected: parseInt(numberOfRowsSelected, 0) || 25,
      filters: {
        ...defaultFilters,
        ...initFilters,
        startDateGte,
        startDateLte,
      },
      pagination: { direction: direction || null, cursor: cursor || undefined },
    };
    this.Compose = adopt({
      query: this.agencySummary,
    });
    this.fetchMore = () => {};
  }

  /**
   * @description
   * Query callback to compose with mutations.
   *
   * @param {*} render - Query's render props.
   * @returns {*} Query component.
   */
  agencySummary = ({ render }) => {
    const { filters, pagination, numberOfRowsSelected } = this.state;
    const { id } = this.props;
    const paginationVariables =
      !pagination.direction || pagination.direction === 'NEXT'
        ? {
            first: numberOfRowsSelected,
            after: pagination.cursor,
          }
        : {
            last: numberOfRowsSelected,
            before: pagination.cursor,
          };
    return (
      <Query
        query={AGENCY_SUMMARY_QUERY}
        variables={{
          id,
          ...filters,
          ...paginationVariables,
        }}
        notifyOnNetworkStatusChange
        fetchPolicy="cache-and-network"
      >
        {({ data, loading, fetchMore }) => {
          this.fetchMore = fetchMore;
          return render({ data, loading, fetchMore });
        }}
      </Query>
    );
  };

  /**
   * @description
   * Filter rows according selected filters.
   *
   * @param {Object} newFilters - Filters to apply.
   */
  handleFilter = newFilters => {
    const history = this.context;
    const { filters } = this.state;
    this.setState({
      filters: { ...filters, ...newFilters },
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);
    const stringified = qs.stringify({
      ...filters,
      ...newFilters,
      numberOfRowsSelected,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Change number of rows selected.
   *
   * @param {number} selectedItem - New number of rows selected.
   */
  handleChangeNumberOfRows = selectedItem => {
    const history = this.context;
    this.setState({
      numberOfRowsSelected: selectedItem,
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      numberOfRowsSelected: selectedItem,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Fetch more rows when user change page.
   *
   * @param {Object} paginatorVariables - Pagination query's variables.
   */
  handleChangePage = ({ direction, cursor }) => {
    const history = this.context;
    this.setState({ pagination: { direction, cursor } });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      direction,
      cursor,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Refresh the list.
   * @returns {Function} - Fetch More function.
   */
  handleRefresh = () => {
    this.fetchMore({
      updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
    });
  };

  /**
   * @description
   * Render AdsList component.
   */
  render() {
    const { numberOfRowsSelected, filters, pagination } = this.state;
    return (
      <this.Compose>
        {({ query: { loading, data } }) => (
          <PaidAdReportDetails
            data={data}
            numberOfRowsSelected={numberOfRowsSelected}
            paginationDirection={pagination.direction}
            changePage={paginationVariables => {
              this.handleChangePage(paginationVariables);
            }}
            changeNumberOfRows={this.handleChangeNumberOfRows}
            appliedFilter={filters}
            filter={this.handleFilter}
            refresh={this.handleRefresh}
            loading={Object.keys(data).length === 0}
            fullLoading={loading}
          />
        )}
      </this.Compose>
    );
  }
}

/**
 * PaidAdReportDetailsContainer PropTypes
 * @param initialFilters - prop initial Filters to apply
 */

PaidAdReportDetailsContainer.propTypes = {
  id: PropTypes.string.isRequired,
  initialFilters: PropTypes.shape({}).isRequired,
};

export default PaidAdReportDetailsContainer;
